/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

const defaultApi = {
  registerCallback: (
    key: string,
    callback: (...args: unknown[]) => Promise<void> | void,
  ): void => {
    throw new Error("registerCallback not implemented");
  },
  executeCallback: (key: string, ...args: unknown[]): Promise<void> | void => {
    throw new Error("executeCallback not implemented");
  },
};

// Create a context to hold the callback functions
export const ExecutorContext = createContext<typeof defaultApi>(defaultApi);

// Executor Provider component
export const ExecutorProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [callbacks, setCallbacks] = useState<
    Record<string, (...args: unknown[]) => Promise<void> | void>
  >({});
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  // Register a callback function with a specific key
  const registerCallback = useCallback(
    (
      key: string,
      callback: ((...args: unknown[]) => Promise<void> | void) | null,
    ) => {
      setCallbacks((prevCallbacks) => ({
        ...prevCallbacks,
        [key]: callback,
      }));
    },
    [],
  );

  // Execute a callback function using the specified key
  const executeCallback = useCallback((key: string, ...args: unknown[]) => {
    const callback = callbacksRef.current[key];
    if (callback) {
      return callback(...args);
    } else {
      console.error(`Callback with key '${key}' not found.`);
    }
  }, []);

  const value = useMemo(
    () => ({ registerCallback, executeCallback }),
    [registerCallback, executeCallback],
  );

  return (
    <ExecutorContext.Provider value={value}>
      {children}
    </ExecutorContext.Provider>
  );
};
